import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { StoreLocation, UserLocation } from 'src/api/types';
import {
  cookieHelper,
  largeExpirationCookieOpts,
  selectedLocationCookieName,
  zustandLocationStorageName
} from 'src/utils/cookieHelper';
import handleAPIError from 'src/api/errors';
import { searchCityByCoordinates } from 'src/api/requests/citiesAPI';
import { IntlShape } from 'react-intl';
import logger from 'src/utils/logger';
import { mountStoreDevtool } from 'simple-zustand-devtools';
import trns from './trns';
import { NotificationProps, NotificationType } from './useNotification';

interface State {
  storeInitilized: boolean;
  userLocation: UserLocation;
  storeLocation: StoreLocation;
  defaultLocation: UserLocation;
  cities: UserLocation[];
  userLocationUpdatedManually: boolean;
  setCities: (cities: UserLocation[]) => void;
  setDefaultLocation: (defaultLocation: UserLocation) => void;
  setLocationToClientCoordinates: (
    showNotification: (params: NotificationProps) => void,
    intl: IntlShape,
    locale: string
  ) => void;
  setUserLocation: (userLocation: UserLocation, isManual?: boolean) => void;
  setStoreLocation: (userLocation: StoreLocation) => void;
  getUserLocationBasedOnCoordinates: (
    position: GeolocationPosition,
    locale: string
  ) => void;
}

const useLocationStore = create<State>()(
  persist(
    (set, get) => ({
      storeInitilized: true,
      userLocation: null,
      storeLocation: null,
      defaultLocation: null,
      cities: [],
      userLocationUpdatedManually: false,
      setCities: (cities: UserLocation[]) => set({ cities }),
      setDefaultLocation: (defaultLocation: UserLocation) =>
        set({ defaultLocation }),
      getUserLocationBasedOnCoordinates: async (
        position: GeolocationPosition,
        locale: string
      ) => {
        const response = await searchCityByCoordinates({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          attr: { locale }
        });
        get().setUserLocation({
          ...response,
          latitude: position.coords?.latitude.toString(),
          longitude: position.coords?.longitude.toString(),
          forceUpdate: !get().userLocation?.forceUpdate
        });
      },
      setLocationToClientCoordinates: async (
        showNotification,
        intl,
        locale
      ) => {
        if (navigator?.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) =>
              get().getUserLocationBasedOnCoordinates(position, locale),
            (error) => {
              logger.log(
                `First geolocation position retrieval failed with error`,
                error
              );
              logger.log('Running another fallback try with longer timeout');
              navigator.geolocation.getCurrentPosition(
                (position) =>
                  get().getUserLocationBasedOnCoordinates(position, locale),
                (locError) => {
                  logger.log(
                    `Second geolocation position retrieval failed with error`,
                    locError
                  );
                  if (showNotification) {
                    showNotification({
                      message: intl.formatMessage(trns.noGeolocationEnabled),
                      type: NotificationType.Warning
                    });
                  }
                },
                { maximumAge: 0, timeout: 8000, enableHighAccuracy: false }
              );
            },
            { maximumAge: 60000, timeout: 1000, enableHighAccuracy: true }
          );
        } else if (showNotification) {
          showNotification({
            message: intl.formatMessage(trns.noGeolocationEnabled),
            type: NotificationType.Warning
          });
        }
      },
      setUserLocation: (locationObject: UserLocation, isManual?: boolean) =>
        set((state) => {
          try {
            cookieHelper.setCookie(
              selectedLocationCookieName,
              JSON.stringify(locationObject),
              largeExpirationCookieOpts.expires
            );
            return {
              userLocation: locationObject,
              userLocationUpdatedManually: !!isManual
            };
          } catch (error) {
            // error might happen if cookie is broken
            handleAPIError(error);
            return state;
          }
        }),
      setStoreLocation: (locationObject: StoreLocation) =>
        set(() => {
          return {
            storeLocation: locationObject
          };
        })
    }),
    {
      name: zustandLocationStorageName
    }
  )
);

export const locationStoreFallback: State = {
  storeInitilized: false,
  userLocation: null,
  storeLocation: null,
  defaultLocation: null,
  cities: [],
  userLocationUpdatedManually: false,
  setCities: () => null,
  setDefaultLocation: () => null,
  setLocationToClientCoordinates: () => null,
  setUserLocation: () => null,
  setStoreLocation: () => null,
  getUserLocationBasedOnCoordinates: () => null
};

if (process.env.NEXT_PUBLIC_ENV === 'dev' && typeof window !== 'undefined') {
  mountStoreDevtool('LocationStore', useLocationStore);
}

export default useLocationStore;
